import React, { useEffect, useState } from "react";
import CardLayout from "components/foundation/templates/CardLayout";
import SelectButton from "components/foundation/SelectButton";
import { CheckIcon } from "assets/icons";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import { DOCUMENT_UUID } from "config/constant";
import useTagsByDocumentCategoryQuery from "features/tag/hooks/queries/useTagsByDocumentCategoryQuery";
import { selectedAnalysisBusinessTagState } from "store/document/analysisDocument";
import FreeTagEditor from "../Create/CreateDocumentForm/CreateDocumentFormHeader/FreeTagEditor";
import { FreeTagDetailDto, TagDetailDto } from "apis/data-contracts";
import { documentFreeTagsState } from "store/document/documentCommon";
import { useUpdateDocumentTags } from "features/document/hooks/mutations/useUpdateDocumentTags";
import Modal from "components/foundation/Modal/Modal";
import useUpdateDocumentFileUrl from "features/pdf/mutations/useUpdateDocumentFileUrl";

interface AnalysisTagEditSelectProps {
  freeTags?: FreeTagDetailDto[];
  tags?: TagDetailDto[];
  onClose: (isSuccess?: boolean) => void;
  uuid: string;
  onTagUpdateSuccess: () => void;
  uploadDocPdfAndGetS3Url?: (fileName: string) => Promise<string>;
}

const AnalysisTagEditSelect = ({
  freeTags,
  tags,
  onClose,
  uuid,
  onTagUpdateSuccess,
  uploadDocPdfAndGetS3Url,
}: AnalysisTagEditSelectProps) => {
  /* 사업구분 태그 */
  const { data: analysisTags } = useTagsByDocumentCategoryQuery(
    DOCUMENT_UUID.ANALYSIS.uuid,
  );
  const busniessTags = analysisTags?.tagCategories[0].tags;

  const [selectedBusinessTag, setSelectedBusinessTag] = useRecoilState(
    selectedAnalysisBusinessTagState,
  );

  const [selectedFreeTags, setSelectedFreeTags] = useRecoilState(
    documentFreeTagsState,
  );

  useEffect(() => {
    if (tags && tags.length > 0) {
      const businessTag = tags.find((tag) => tag.categoryName === "사업구분");
      if (businessTag) {
        setSelectedBusinessTag(businessTag);
      }
    }
  }, [tags, setSelectedBusinessTag]);

  useEffect(() => {
    if (freeTags && freeTags.length === 0) {
      setSelectedFreeTags([]);
    } else if (freeTags && freeTags.length > 0) {
      setSelectedFreeTags(freeTags);
    }
  }, [freeTags, setSelectedFreeTags]);

  /* 완료 버튼 */
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { mutate: updateDocumentTags } = useUpdateDocumentTags();
  const { mutate: updateDocumentFileUrl } = useUpdateDocumentFileUrl();

  const onClickConfirm = () => {
    setIsOpenModal(true);
  };

  const handleUpdateTags = async () => {
    updateDocumentTags(
      {
        uuid: uuid,
        data: {
          tagUuids: [selectedBusinessTag?.tagUuid],
          freeTagUuids:
            selectedFreeTags.length > 0
              ? selectedFreeTags.map((tag) => tag.uuid)
              : [],
        },
      },
      {
        onSuccess: async () => {
          onClose(true);

          // PDF 업데이트
          if (uploadDocPdfAndGetS3Url) {
            try {
              const s3Url = await uploadDocPdfAndGetS3Url("octo.pdf");
              updateDocumentFileUrl(
                {
                  uuid: uuid,
                  data: {
                    documentFileUrl: s3Url,
                  },
                },
                {
                  onSuccess: () => {
                    console.log("PDF URL 업데이트 완료");
                  },
                },
              );
            } catch (error) {
              console.error("PDF 업데이트 실패:", error);
            }
          }

          // 성공 콜백 실행
          await onTagUpdateSuccess();
        },
      },
    );
  };

  return (
    <>
      <CardLayout.Container
        header={{
          title: "태그 수정",
          description:
            "문서에 들어갈 기본 태그를 수정 또는 추가할 수 있습니다.",
        }}
        gap={40}
        headerColor="#171D23"
      >
        <DocumentAnalysisCreateForm
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <CardLayout.Field label="자유태그">
            <FreeTagEditor isTitle={false} />
          </CardLayout.Field>
          <CardLayout.Field label="사업구분">
            <SelectContainerStyle>
              {busniessTags?.map((tag) => {
                const selected = tag.tagUuid === selectedBusinessTag?.tagUuid;

                return (
                  <SelectButton
                    key={tag.tagUuid}
                    type="button"
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => setSelectedBusinessTag(tag)}
                  >
                    {tag.tagName}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>
        </DocumentAnalysisCreateForm>
        <Divider />
        <ButtonWrapperStyle>
          <Button
            variant="tertiary"
            size="large"
            fill
            onClick={() => onClose()}
          >
            취소
          </Button>
          <Button variant="primary" size="large" fill onClick={onClickConfirm}>
            완료
          </Button>
        </ButtonWrapperStyle>
      </CardLayout.Container>
      <Modal.Root open={isOpenModal} onOpenChange={setIsOpenModal}>
        <Modal.Body
          onConfirm={handleUpdateTags}
          onCancel={() => setIsOpenModal(false)}
          title="태그를 수정하시겠습니까?"
        ></Modal.Body>
      </Modal.Root>
    </>
  );
};

export default AnalysisTagEditSelect;

/* Styles */
const DocumentAnalysisCreateForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 5rem;
`;

const SelectContainerStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14.2rem, 1fr));
  gap: 1rem;
`;

const ButtonWrapperStyle = styled.div`
  display: flex;
  gap: 1rem;
`;
